import { encodePacked, zeroAddress } from 'viem'
import { getEvmChain } from '@1delta/providers'
import { RawRpcCall } from '../utils/rpcCall'
import {
  TokenBalanceQuery,
  PreparedTokenBalanceRpcCalls,
  ParsedBalanceData,
  TokenBalanceResult,
} from './types'

function balanceFetcher() {
  return '0xba1a60c7B0e784Bf25F731Ca9fcA6762fFd63a11'
}

// The balance fetcher uses a custom function selector that is essentially ignored
// We encode just the selector bytes followed by our packed data
const BALANCE_FETCHER_SELECTOR = '0x30fb0985' // bytes4(keccak256("1delta(bytes)"))

/**
 * Encodes the calldata for the balance fetcher contract
 * Format: [uint16 numTokens][uint16 numAccounts][addresses...][tokens...]
 */
export function encodeBalanceFetcherCalldata(
  accounts: string[],
  tokens: string[],
): string {
  if (accounts.length < 1 || tokens.length < 1) {
    return '0x'
  }

  // Lowercase addresses to avoid checksum validation issues
  const normalizedAccounts = accounts.map(
    (a) => a.toLowerCase() as `0x${string}`,
  )
  const normalizedTokens = tokens.map((t) => t.toLowerCase() as `0x${string}`)

  const header = encodePacked(
    ['uint16', 'uint16'],
    [normalizedTokens.length, normalizedAccounts.length],
  )

  const accountsEncoded = encodePacked(
    normalizedAccounts.map(() => 'address' as const),
    normalizedAccounts,
  ).slice(2)

  const tokensEncoded = encodePacked(
    normalizedTokens.map(() => 'address' as const),
    normalizedTokens,
  ).slice(2)

  return header + accountsEncoded + tokensEncoded
}

/**
 * Decodes ABI-encoded bytes from the RPC response
 * Response format: [offset 32 bytes][length 32 bytes][data padded to 32 bytes]
 */
function decodeAbiEncodedBytes(hexData: string): string {
  const data = hexData.startsWith('0x') ? hexData.slice(2) : hexData

  // Skip offset (32 bytes = 64 hex chars)
  // Read length (32 bytes = 64 hex chars)
  const lengthHex = data.slice(64, 128)
  const length = parseInt(lengthHex, 16)

  // Extract actual data (length bytes = length * 2 hex chars)
  const actualData = data.slice(128, 128 + length * 2)

  return actualData
}

/**
 * Parses the raw hex response from the balance fetcher contract
 * Handles both ABI-encoded bytes response and raw packed data
 * Returns parsed balance data with block number and per-user balances
 */
export function parseBalanceFetcherResult(
  hexData: string,
  users: string[],
  tokens: string[],
): ParsedBalanceData {
  let data = hexData.startsWith('0x') ? hexData.slice(2) : hexData

  // Check if response is ABI-encoded bytes (starts with offset 0x20 padded to 32 bytes)
  // ABI-encoded responses have offset at position 0 (always 0x20 = 32 for single bytes param)
  if (
    data.length > 128 &&
    data.slice(0, 64) ===
      '0000000000000000000000000000000000000000000000000000000000000020'
  ) {
    data = decodeAbiEncodedBytes(data)
  }

  let offset = 0

  // First 8 bytes (16 hex chars) = block number
  const blockNumberHex = data.slice(offset, offset + 16)
  const blockNumber = BigInt('0x' + blockNumberHex)
  offset += 16

  const results: ParsedBalanceData['balances'] = []

  while (offset < data.length) {
    // Check if we have enough data for user header (4 bytes = 8 hex chars)
    if (offset + 8 > data.length) break

    // 2 bytes (4 hex chars) = user index
    const userIndexHex = data.slice(offset, offset + 4)
    const userIndex = parseInt(userIndexHex, 16)
    offset += 4

    // 2 bytes (4 hex chars) = count of non-zero balances
    const countHex = data.slice(offset, offset + 4)
    const count = parseInt(countHex, 16)
    offset += 4

    const balances: ParsedBalanceData['balances'][0]['balances'] = {}

    for (let i = 0; i < count; i++) {
      // Check if we have enough data for balance entry (16 bytes = 32 hex chars)
      if (offset + 32 > data.length) break

      // 2 bytes (4 hex chars) = token index
      const tokenIndexHex = data.slice(offset, offset + 4)
      const tokenIndex = parseInt(tokenIndexHex, 16)
      offset += 4

      // 14 bytes (28 hex chars) = balance (uint112)
      const balanceHex = data.slice(offset, offset + 28)
      const balance = balanceHex ? BigInt('0x' + balanceHex) : 0n
      offset += 28

      const tokenAddress = tokens[tokenIndex]
      if (tokenAddress) {
        balances[tokenAddress] = {
          tokenIndex,
          tokenAddress,
          balance,
        }
      }
    }

    results.push({
      userIndex,
      userAddress: users[userIndex],
      balances,
    })
  }

  return {
    balances: results,
    blockNumber,
  }
}

/**
 * Prepares the RPC call for fetching token balances without executing it
 * Uses the balance fetcher contract for efficient batched balance queries
 *
 * @param chainId - The chain ID
 * @param account - The account address to query balances for
 * @param tokens - Array of token addresses (use zeroAddress for native balance)
 * @param blockTag - Block tag for the RPC call, default is 'latest'
 * @returns The prepared RPC call and metadata needed for parsing
 */
export function prepareTokenBalanceRpcCalls(
  chainId: string,
  account: string,
  tokens: string[],
  blockTag = 'latest',
): PreparedTokenBalanceRpcCalls {
  // Normalize addresses to lowercase for consistency
  const normalizedAccount = account.toLowerCase()
  const normalizedTokens = tokens.map((t) => t.toLowerCase())

  // Include zeroAddress to fetch native balance along with tokens
  const tokensWithNative = [zeroAddress, ...normalizedTokens]

  const encodedCalldata = encodeBalanceFetcherCalldata(
    [normalizedAccount],
    tokensWithNative,
  )

  // Build the full calldata: selector + ABI-encoded bytes parameter
  // The balance fetcher expects: function 1delta(bytes calldata data) external
  // ABI encoding for bytes: offset (32 bytes) + length (32 bytes) + data (padded to 32 bytes)
  const dataLength = (encodedCalldata.length - 2) / 2 // bytes length
  const paddedLength = Math.ceil(dataLength / 32) * 32

  const offsetHex =
    '0000000000000000000000000000000000000000000000000000000000000020' // offset = 32
  const lengthHex = dataLength.toString(16).padStart(64, '0')
  const dataHex = encodedCalldata.slice(2).padEnd(paddedLength * 2, '0')

  const fullCalldata =
    BALANCE_FETCHER_SELECTOR + offsetHex + lengthHex + dataHex

  const call: RawRpcCall = {
    jsonrpc: '2.0',
    id: 1,
    method: 'eth_call',
    params: [
      {
        to: balanceFetcher(),
        data: fullCalldata,
      },
      blockTag,
    ],
  }

  return {
    call,
    query: {
      chainId,
      account: normalizedAccount,
      tokens: tokensWithNative,
    },
    encodedCalldata,
  }
}

/**
 * Converts the raw RPC result into a structured token balance map
 *
 * @param rawResult - The raw hex result from the RPC call
 * @param query - The original query parameters
 * @returns Map of token address to balance data
 */
export function parseTokenBalanceResult(
  rawResult: string,
  query: TokenBalanceQuery,
): TokenBalanceResult {
  if (!rawResult || rawResult === '0x') {
    return {}
  }

  const parsed = parseBalanceFetcherResult(
    rawResult,
    [query.account],
    query.tokens,
  )

  if (parsed.balances.length === 0) {
    return {}
  }

  const result: TokenBalanceResult = {}
  const userBalances = parsed.balances[0].balances

  for (const token of query.tokens) {
    const balanceEntry = userBalances[token]
    if (balanceEntry) {
      result[token] = {
        balanceRaw: balanceEntry.balance.toString(),
        balance: balanceEntry.balance,
      }
    } else {
      result[token] = {
        balanceRaw: '0',
        balance: 0n,
      }
    }
  }

  return result
}

export interface FetchTokenBalancesOptions {
  blockTag?: string
  rpcUrl?: string
  rpcUrls?: string[]
  maxRetries?: number
}

/**
 * Makes a raw JSON-RPC call with retry logic and RPC URL rotation.
 * On failure (HTTP error, JSON-RPC error, or network error), rotates
 * to the next URL in the list and retries.
 */
async function rpcFetchWithRetry(
  rpcUrls: string[],
  body: string,
  maxRetries: number,
): Promise<string> {
  let lastError: Error | undefined

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const url = rpcUrls[attempt % rpcUrls.length]
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      })

      if (!res.ok) {
        lastError = new Error(`RPC ${res.status} ${res.statusText}`)
        continue
      }

      const json = (await res.json()) as { result?: string; error?: any }

      if (json.error) {
        lastError = new Error(`RPC error: ${JSON.stringify(json.error)}`)
        continue
      }

      return json.result ?? '0x'
    } catch (e) {
      lastError = e instanceof Error ? e : new Error(String(e))
    }
  }

  throw lastError ?? new Error('All RPC attempts failed')
}

/**
 * End-to-end function that fetches token balances for an account.
 * Combines prepareTokenBalanceRpcCalls and parseTokenBalanceResult
 * with retry logic and RPC URL rotation.
 *
 * @param chainId - The chain ID
 * @param account - The account address to query balances for
 * @param tokens - Array of token addresses
 * @param options - Optional configuration (blockTag, rpcUrl/rpcUrls, maxRetries)
 * @returns Map of token address to balance data
 */
export async function fetchTokenBalances(
  chainId: string,
  account: string,
  tokens: string[],
  options: FetchTokenBalancesOptions = {},
): Promise<TokenBalanceResult> {
  const { blockTag = 'latest', rpcUrl, rpcUrls, maxRetries = 3 } = options

  const prepared = prepareTokenBalanceRpcCalls(
    chainId,
    account,
    tokens,
    blockTag,
  )

  // Build URL list: explicit rpcUrls > single rpcUrl > chain default
  let urls: string[]
  if (rpcUrls?.length) {
    urls = rpcUrls
  } else if (rpcUrl) {
    urls = [rpcUrl]
  } else {
    const chain = getEvmChain(chainId)
    const defaultUrl = chain.rpcUrls?.default?.http?.[0]
    if (!defaultUrl) {
      throw new Error(`No RPC URL available for chain ${chainId}`)
    }
    urls = [defaultUrl]
  }

  const rawResult = await rpcFetchWithRetry(
    urls,
    JSON.stringify(prepared.call),
    maxRetries,
  )

  return parseTokenBalanceResult(rawResult, prepared.query)
}
